import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BookingLink } from '@/components/BookingLink';
import { Card } from '@/components/Card';
import { Chip, ChipRow } from '@/components/ChipRow';
import { ProvenanceNote } from '@/components/Provenance';
import { api } from '@/lib/api';
import type { Airport, Service } from '@shared/types';

export function AirportPage() {
  const { iata } = useParams();
  const [airport, setAirport] = useState<Airport | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!iata) return;
    api
      .airport(iata)
      .then((data) => {
        setAirport(data.airport);
        setServices(data.services);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Airport failed to load.'));
  }, [iata]);

  if (error) return <p className="text-sm text-alert">{error}</p>;
  if (!airport) return <p className="text-sm text-muted">Loading airport…</p>;

  return (
    <div className="space-y-4">
      <Card>
        <ChipRow>
          <Chip tone="navy">{airport.iata}</Chip>
          <Chip>{airport.city}</Chip>
          {airport.hasAirlineLounge ? <Chip tone="success">Airline lounge in seed</Chip> : <Chip>No airline lounge</Chip>}
        </ChipRow>
        <h2 className="mt-3 font-display text-[30px] leading-none text-navy">{airport.name}</h2>
        <p className="mt-3 text-sm leading-relaxed text-muted">{airport.playbookSummary}</p>
        {airport.uaNotes && <p className="mt-2 text-sm">{airport.uaNotes}</p>}
        <ProvenanceNote className="mt-3" provenance={airport.provenance} />
      </Card>
      {services.map((service) => (
        <Card key={service.id}>
          <ChipRow>
            <Chip>{service.kind}</Chip>
            {service.terminal && <Chip tone="brass">{service.terminal}</Chip>}
            {service.alignedWithUnited === 1 && <Chip tone="navy">United-aligned</Chip>}
          </ChipRow>
          <h3 className="mt-2 font-display text-2xl text-navy">{service.name}</h3>
          <p className="mt-1 text-sm text-muted">{service.description}</p>
          <p className="mt-2 text-[13px]">{service.eligibilityNotes}</p>
          {service.locationNotes && <p className="mt-1 text-[13px] text-muted">{service.locationNotes}</p>}
          {service.bookingNotes && <p className="mt-1 text-[13px] text-muted">{service.bookingNotes}</p>}
          <BookingLink href={service.bookingUrl} kind={service.kind} />
          {service.hoursNotes && <p className="mt-1 text-[13px] text-muted">{service.hoursNotes}</p>}
          {service.prices.length > 0 && (
            <ul className="mt-3 space-y-1 text-[13px]">
              {service.prices.map((price) => (
                <li key={price.id}>
                  {price.amountNote || price.label}
                  {price.provenance.verifiedAt ? ` · snapshot ${price.provenance.verifiedAt}` : ''}
                </li>
              ))}
            </ul>
          )}
          <ProvenanceNote className="mt-2" provenance={service.provenance} />
        </Card>
      ))}
      <Link to="/trips/new" className="block rounded-card bg-navy px-4 py-3 text-center text-sm font-semibold text-sand">
        Plan a trip through {airport.iata}
      </Link>
    </div>
  );
}
